/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Model Image Studio Model Selector Component (Task 10.4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { useImageStudioStore, IMAGE_MODELS } from '../../stores/useImageStudioStore';
import { useI18nStore } from '../../stores/useI18nStore';
import { Sparkles, Zap, Check } from 'lucide-react';

export const ImageModelSelector: React.FC = () => {
  const { t } = useI18nStore();
  const { selectedModel, setSelectedModel } = useImageStudioStore();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-xs font-heading font-bold text-foreground flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-primary" />
          <span>{t('media.selectModelTitle')}</span>
        </label>
        <span className="text-[11px] text-foreground-muted">
          {t('media.multiModelAi')}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {IMAGE_MODELS.map((model) => {
          const isSelected = selectedModel === model.id;
          return (
            <button
              key={model.id}
              type="button"
              onClick={() => setSelectedModel(model.id)}
              className={`p-3.5 rounded-2xl border text-left flex flex-col justify-between transition-all duration-200 relative group ${
                isSelected
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/40 shadow-glow-primary/20'
                  : 'border-surface-border bg-surface hover:bg-surface-elevated hover:border-surface-border/80'
              }`}
            >
              {isSelected && (
                <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary text-primary-fg flex items-center justify-center">
                  <Check className="w-3 h-3 stroke-[3]" />
                </div>
              )}

              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-heading font-extrabold text-foreground">
                    {model.name}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-accent/15 text-accent border border-accent/20">
                    {model.tag}
                  </span>
                </div>
                <p className="text-[11px] text-foreground-muted leading-relaxed line-clamp-2">
                  {model.description}
                </p>
              </div>

              <div className="flex items-center justify-between pt-3 mt-3 border-t border-surface-border/60">
                <span className="text-[10px] text-foreground-muted font-medium">
                  {model.provider}
                </span>
                <div className="flex items-center gap-1 text-xs font-mono font-bold text-primary">
                  <Zap className="w-3.5 h-3.5" />
                  <span>{model.creditCost} credits</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
